import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { isObject, validationErrorsToString } from '../../utils';

// PACKAGES
import { useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import moment from 'moment';

// COMPONENTS
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import GlobalModal from '../../components/GlobalModal';
import AdminsModal from './Modals/AdminsModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import CircleImage from '../../components/CircleImage';
import DeActivateModal from './Modals/DeActivateModal';
import EnableModal from './Modals/EnableModal';
import ExportDataModal from './Modals/ExportDataModal';
import InitiateDeleteOrganisationModal from './Modals/InitiateDeleteOrganisationModal';
import DeclineDeleteOrganisationModal from './Modals/DeclineDeleteOrganisationModal';
import ApproveDeleteOrganisationModal from './Modals/ApproveDeleteOrganisationModal';
import DisplayDateTime from '../../components/DisplayDateTime';
import DisplayPhoneNumber from '../../components/DisplayPhoneNumber';
import StatusIcon from '../../components/StatusIcon';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as CopyIcon } from '../../assets/svg/copy.svg';
import Spinner from '../../assets/img/spinner.gif';

// TYPES
import {
	OrganisationDetailsOrganisation,
	DomainOrganisationStatus,
	DomainOrganisationExportStatus,
	OrganisationExportStatusResponse,
	DomainOrganisationDeletionStatus,
	OrganisationDeletionStatusResponse
} from '../../api/models';

const initialMaxPayments = 5;
const loadMoreIncrement = 10;
const exportPollInterval = 3000;

const isExportInProgress = (status?: OrganisationExportStatusResponse | null) =>
	!!status &&
	!status.isStale &&
	(status.status === DomainOrganisationExportStatus.Pending ||
		status.status === DomainOrganisationExportStatus.Processing);

const isExportReadyToCopy = (status?: OrganisationExportStatusResponse | null) =>
	!!status && status.status === DomainOrganisationExportStatus.Completed && status.isLinkValid === true;

const isDeletionActive = (status?: OrganisationDeletionStatusResponse | null) => !!status?.hasActiveRequest;

const canReviewDeletion = (status?: OrganisationDeletionStatusResponse | null) =>
	!!status?.hasActiveRequest &&
	!status.isInitiator &&
	status.status === DomainOrganisationDeletionStatus.PendingApproval;

const CompaniesDetail = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showAdminsModal, setShowAdminsModal] = React.useState<boolean>(false);

	const [showDeActivateModal, setShowDeActivateModal] = React.useState<boolean>(false);

	const [showEnableModal, setShowEnableModal] = React.useState<boolean>(false);

	const [showExportModal, setShowExportModal] = React.useState<boolean>(false);

	const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);

	const [showDeclineDeleteModal, setShowDeclineDeleteModal] = React.useState<boolean>(false);

	const [showApproveDeleteModal, setShowApproveDeleteModal] = React.useState<boolean>(false);

	const [deletionStatus, setDeletionStatus] = React.useState<OrganisationDeletionStatusResponse | null>(null);

	const [showDeletionPending, setShowDeletionPending] = React.useState<boolean>(false);

	const [deletionPending, setDeletionPending] = React.useState<boolean>(false);

	// State for clean up function
	const [isActive, setIsActive] = React.useState<boolean>(false);

	const [maxPaymentItems, setMaxPaymentItems] = React.useState<number>(initialMaxPayments);

	const [exportStatus, setExportStatus] = React.useState<OrganisationExportStatusResponse | null>(null);

	const [showExportProcessing, setShowExportProcessing] = React.useState<boolean>(false);

	const [showExportedData, setShowExportedData] = React.useState<boolean>(false);

	const [exportDataReady, setExportDataReady] = React.useState<boolean>(false);

	const [copiedField, setCopiedField] = React.useState<string>('');

	const pollTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	// Get the id from params.
	const params = useParams<{ id: string }>();

	// Set up data state and initial data
	const initialData: OrganisationDetailsOrganisation = {
		id: undefined,
		status: undefined,
		name: undefined,
		type: undefined,
		logo: undefined,
		australianBusinessNumber: undefined,
		numberOfChildren: undefined,
		address1: undefined,
		address2: undefined,
		phoneNumber: undefined,
		email: undefined,
		city: undefined,
		postcode: undefined,
		state: undefined,
		administrators: undefined,
		payments: [],
		statusDate: undefined,
		identifier: undefined
	};

	const [companiesDetailData, setCompaniesDetailData] = React.useState<OrganisationDetailsOrganisation>(initialData);

	const clearPollTimer = () => {
		if (pollTimer.current) {
			clearTimeout(pollTimer.current);
			pollTimer.current = null;
		}
	};

	const applyExportStatus = (status: OrganisationExportStatusResponse | null, deletionActive: boolean): boolean => {
		if (deletionActive) {
			setShowExportProcessing(false);
			setShowExportedData(false);
			setExportDataReady(false);
			return false;
		}

		setExportStatus(status);

		if (isExportReadyToCopy(status)) {
			setShowExportProcessing(false);
			setShowExportedData(true);
			setExportDataReady(true);
			return false;
		}

		setShowExportedData(false);
		setExportDataReady(false);

		if (status?.status === DomainOrganisationExportStatus.Failed) {
			setShowExportProcessing(false);
			setFormErrorMessage(status.errorMessage || 'Export failed');
			return false;
		}

		if (isExportInProgress(status)) {
			setShowExportProcessing(true);
			return true;
		}

		setShowExportProcessing(false);
		return false;
	};

	const applyDeletionStatus = (status: OrganisationDeletionStatusResponse | null) => {
		setDeletionStatus(status);

		const active = isDeletionActive(status);

		setDeletionPending(active);
		setShowDeletionPending(active);
	};

	const pollExportStatus = async (deletionActive = isDeletionActive(deletionStatus)) => {
		clearPollTimer();

		const response = await apibridge.getOrganisationExportStatus(params.id);

		if (isObject(response) && response.isError) {
			setShowExportProcessing(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);

			return;
		}

		const shouldContinue = applyExportStatus(
			response as OrganisationExportStatusResponse | null,
			isDeletionActive(deletionStatus)
		);

		if (shouldContinue) {
			pollTimer.current = setTimeout(() => {
				pollExportStatus();
			}, exportPollInterval);
		}
	};

	const loadPageData = async () => {
		setIsLoading(true);
		setFormErrorMessage('');

		const [detailResponse, exportStatusResponse, deletionStatusResponse] = await Promise.all([
			apibridge.getCompaniesDetailData(params.id),
			apibridge.getOrganisationExportStatus(params.id),
			apibridge.getOrganisationDeletionStatus(params.id)
		]);

		if (detailResponse.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				detailResponse.validationErrors.length
					? validationErrorsToString(detailResponse.validationErrors)
					: detailResponse.message || 'An error has occurred'
			);
		} else if (detailResponse.result) {
			setIsLoading(false);

			setIsActive(detailResponse.result.status === DomainOrganisationStatus.Active);

			setMaxPaymentItems(initialMaxPayments);

			setCompaniesDetailData(detailResponse.result);
		} else {
			setIsLoading(false);
		}

		let deletionActive = false;

		if (!(isObject(deletionStatusResponse) && deletionStatusResponse.isError)) {
			const status = deletionStatusResponse as OrganisationDeletionStatusResponse | null;

			applyDeletionStatus(status);
			deletionActive = isDeletionActive(status);
		}

		if (!(isObject(exportStatusResponse) && exportStatusResponse.isError)) {
			const shouldPoll = applyExportStatus(
				exportStatusResponse as OrganisationExportStatusResponse | null,
				deletionActive
			);

			if (shouldPoll) {
				pollTimer.current = setTimeout(() => {
					pollExportStatus(deletionActive);
				}, exportPollInterval);
			}
		}
	};

	const handleDeletionRequested = async () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
		setFormErrorMessage('');

		const response = await apibridge.getOrganisationDeletionStatus(params.id);

		if (isObject(response) && response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);

			return;
		}

		const status = response as OrganisationDeletionStatusResponse | null;

		applyDeletionStatus(status);
		applyExportStatus(exportStatus, isDeletionActive(status));
	};

	const handleDeclineComplete = async () => {
		await loadPageData();
	};

	const handleExportStarted = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
		setShowExportProcessing(true);
		setShowExportedData(false);
		setExportDataReady(false);
		setFormErrorMessage('');
		clearPollTimer();
		pollExportStatus();
	};

	const copyToClipboard = async (value: string, field: string) => {
		try {
			await navigator.clipboard.writeText(value);
			setCopiedField(field);

			setTimeout(() => {
				setCopiedField('');
			}, 3000);
		} catch {
			setCopiedField('error');

			setTimeout(() => {
				setCopiedField('');
			}, 3000);
		}
	};

	const getCompaniesDetailData = async () => {
		await loadPageData();
	};

	React.useEffect(() => {
		loadPageData();

		return () => {
			clearPollTimer();
		};
	}, []);

	const {
		status,
		name,
		type,
		logo,
		australianBusinessNumber,
		numberOfChildren,
		address1,
		address2,
		phoneNumber,
		email,
		city,
		postcode,
		state,
		administrators,
		payments,
		amount,
		paymentCycle,
		pricePerChild,
		statusDate,
		identifier
	} = companiesDetailData;

	const actionsDisabled = exportDataReady || deletionPending || showExportProcessing;
	// Enable stays available while exported data is ready to copy; only block during processing/deletion
	const isEnableOrganisationBlocked = deletionPending || showExportProcessing;

	// Format ABN
	const formatABN = (abn?: string) => {
		if (abn) {
			const parts = abn.toString().split('.');

			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

			return parts.join('.');
		}
	};

	return (
		<div className="page-general-layout settings-page companies-detail-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					{isLoading ? (
						<LoadingSpinner show={isLoading} />
					) : (
						<>
							{showDeletionPending && deletionStatus && (
								<Alert color="danger" className="w-100 mbtom-10 deletion-pending-panel">
									<div className="alert-icon">
										<StatusIcon statusType="danger" />
									</div>

									<div className="alert-content">
										<p className="body-normal fw-bold mb-0">Deletion of organisation is awaiting approval</p>

										{deletionStatus.isInitiator ? (
											<p className="body-medium fw-medium mb-0">
												Super Administrators have been notified via email to <strong>approve</strong> or{' '}
												<strong>decline</strong> the request.
											</p>
										) : (
											<p className="body-medium fw-medium mb-0">
												Please <strong>approve</strong> or <strong>decline</strong> the request
											</p>
										)}

										{deletionStatus.expiresAt && (
											<p className="body-medium mb-0">
												<span className="fw-bold">Approval expiry:</span>{' '}
												<DisplayDateTime format="DD/MM/YYYY, hh:mm A">{deletionStatus.expiresAt}</DisplayDateTime>
											</p>
										)}
									</div>
								</Alert>
							)}

							{showExportedData && exportStatus && (
								<Alert color="info" className="w-100 mbtom-20 export-data-panel">
									<div className="alert-icon">
										<StatusIcon statusType="export" />
									</div>

									<div className="alert-content">
										<p className="body-normal fw-bold mb-0">Exported data</p>

										{exportStatus.shareUrl && (
											<div className="export-data-row export-data-row-url">
												<div className="export-data-label">
													<span className="export-data-label-text body-medium">Upload URL:</span>
													<span className="body-medium export-data-value export-data-value-truncate">
														{exportStatus.shareUrl}
													</span>
												</div>

												<Button
													className="btn-secondary small export-data-copy-btn"
													onClick={() => copyToClipboard(exportStatus.shareUrl || '', 'url')}
												>
													{copiedField === 'url' ? (
														'Copied!'
													) : (
														<>
															<CopyIcon className="link-icon" />
															Copy
														</>
													)}
												</Button>
											</div>
										)}

										{exportStatus.sharePassword && (
											<div className="export-data-row">
												<div className="export-data-label">
													<span className="export-data-label-text body-medium">Password:</span>
													<span className="body-medium export-data-value">{exportStatus.sharePassword}</span>
												</div>

												<Button
													className="btn-secondary small export-data-copy-btn"
													onClick={() => copyToClipboard(exportStatus.sharePassword || '', 'password')}
												>
													{copiedField === 'password' ? (
														'Copied!'
													) : (
														<>
															<CopyIcon className="link-icon" />
															Copy
														</>
													)}
												</Button>
											</div>
										)}

										{exportStatus.linkExpiresAt && (
											<div className="export-data-row">
												<div className="export-data-label">
													<span className="export-data-label-text body-medium">Expiry date:</span>
													<span className="body-medium export-data-value">
														<DisplayDateTime format="DD/MM/YYYY, hh:mm A">{exportStatus.linkExpiresAt}</DisplayDateTime>
													</span>
												</div>

												<Button
													className="btn-secondary small export-data-copy-btn"
													onClick={() =>
														copyToClipboard(moment(exportStatus.linkExpiresAt).format('DD/MM/YYYY, hh:mm A'), 'expiry')
													}
												>
													{copiedField === 'expiry' ? (
														'Copied!'
													) : (
														<>
															<CopyIcon className="link-icon" />
															Copy
														</>
													)}
												</Button>
											</div>
										)}
									</div>
								</Alert>
							)}

							{showExportProcessing && (
								<Alert color="info" className="w-100 mbtom-20 export-status-alert">
									<div className="alert-icon">
										<StatusIcon statusType="export" />
									</div>

									<div className="alert-content">
										<p className="body-normal fw-bold mb-0">Exporting data</p>

										<p className="body-medium fw-medium mb-0">Please wait while we compile the export data files...</p>
									</div>

									<div className="export-processing-status">
										<img className="spinner" src={Spinner} alt="spinner" />
										Processing...
									</div>
								</Alert>
							)}

							{status === DomainOrganisationStatus.Inactive && (
								<Alert color="danger" className="w-100 mbtom-48">
									<div className="alert-icon">
										<StatusIcon statusType="danger" />
									</div>

									<div className="alert-content">
										<p className="body-normal fw-bold mb-0">This organisation is deactivated</p>

										{statusDate && (
											<p className="body-medium fw-medium mb-0">
												As of <DisplayDateTime format="DD/MM/YYYY, hh:mm A">{statusDate}</DisplayDateTime>
											</p>
										)}
									</div>
								</Alert>
							)}

							{status === DomainOrganisationStatus.Pending && (
								<div className="deactive-message">
									<h2 className="main-purple-text">This company is incomplete</h2>
								</div>
							)}

							<CircleImage size={80} src={logo} alt="organisation logo" />

							<h2 className="header-text settings-page">{name}</h2>

							<div className="general-info">
								<span className="header-info">{type}</span>

								{type && australianBusinessNumber && <div className="header-dot"></div>}

								<span className="header-info">{formatABN(australianBusinessNumber)}</span>
							</div>

							<article className={`children-quantity w-100 mtop-40 ${isActive ? '' : 'deactivated'}`}>
								<label htmlFor="childrenQuantityInput" className="children-quantity-title">
									Number of children at your organisation
								</label>

								<div className="number-container">
									<div className="quantity-input">{numberOfChildren}</div>
								</div>
							</article>

							<div className={`companies-detail-section ${isActive ? '' : 'deactivated'}`}>
								<h2 className="detail-title">Details</h2>

								<ul>
									<li className="row detail-row">
										<div className="col-4 detail-name">ABN</div>

										<div className="col-8 detail-info">{formatABN(australianBusinessNumber)}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Identifier</div>

										<div className="col-8 detail-info">{identifier}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Phone number</div>

										<div className="col-8 detail-info">
											<DisplayPhoneNumber>{phoneNumber}</DisplayPhoneNumber>
										</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Email</div>

										<div className="col-8 detail-info">{email}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Address line 1</div>

										<div className="col-8 detail-info">{address1}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Address line 2</div>

										<div className="col-8 detail-info">{address2}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">City</div>

										<div className="col-8 detail-info">{city}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">State</div>

										<div className="col-8 detail-info">{state}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Postcode</div>

										<div className="col-8 detail-info">{postcode}</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">ChildAware Leaders</div>

										<div className="col-8 detail-info">
											{administrators}

											{isActive && (
												<Button className="btn-text pdr-0 pdt-0 pdb-0" onClick={() => setShowAdminsModal(true)}>
													Manage
												</Button>
											)}
										</div>
									</li>

									<li className="row detail-row">
										<div className="col-4 detail-name">Subscription</div>

										<div className="col-8 detail-info">
											{amount && (
												<>
													{type} Plan, ${amount} &nbsp;
													<span className="extra-info">{paymentCycle}</span>
												</>
											)}
										</div>
									</li>
								</ul>
							</div>

							{payments && payments.length > 0 && (
								<div className="billing-tab">
									<div className={`payment-history ${isActive ? '' : 'deactivated'}`}>
										<h2 className="payment-title">Payment History</h2>

										<div className="row table-header">
											<div className="col-9 col-lg-10 col-name">Date</div>

											<div className="col-3 col-lg-2 col-name text-align-right">Amount</div>
										</div>

										<ul>
											{payments.slice(0, maxPaymentItems).map((item, index) => {
												return (
													<li key={index} className="row table-row">
														<div className="col-9 col-lg-10 payment-date">
															<DisplayDate>{item.paymentDate}</DisplayDate>
														</div>

														<div className="col-3 col-lg-2 payment-amount align-right">${item.amount?.toFixed(2)}</div>
													</li>
												);
											})}
										</ul>

										{maxPaymentItems < payments.length && (
											<Button
												className="btn-secondary btn-show-more"
												onClick={() =>
													setMaxPaymentItems((prev) => Math.min(prev + loadMoreIncrement, payments.length))
												}
												buttonIcon={<PlusIcon className="link-icon" />}
											>
												Show {Math.min(loadMoreIncrement, payments.length - maxPaymentItems)} more
											</Button>
										)}
									</div>
								</div>
							)}

							{status === DomainOrganisationStatus.Inactive && canReviewDeletion(deletionStatus) && (
								<div className="form-two-button-group companies-detail-actions companies-detail-deletion-review">
									<Button className="btn-secondary btn-danger-outline" onClick={() => setShowDeclineDeleteModal(true)}>
										Decline organisation deletion
									</Button>

									<Button className="btn-danger-solid" onClick={() => setShowApproveDeleteModal(true)}>
										Approve organisation deletion
									</Button>
								</div>
							)}

							{status === DomainOrganisationStatus.Inactive && !canReviewDeletion(deletionStatus) && (
								<div className="form-two-button-group companies-detail-actions">
									<div className="companies-detail-actions-left">
										<Button
											className="btn-secondary"
											disabled={actionsDisabled}
											onClick={() => setShowExportModal(true)}
										>
											Export data
										</Button>

										<Button
											className="btn-secondary btn-danger-outline"
											disabled={actionsDisabled}
											onClick={() => setShowDeleteModal(true)}
										>
											Delete organisation
										</Button>
									</div>

									<Button
										className="btn-primary"
										disabled={isEnableOrganisationBlocked}
										onClick={() => setShowEnableModal(true)}
									>
										Activate organisation
									</Button>
								</div>
							)}

							{status === DomainOrganisationStatus.Active && (
								<div className="form-button-group">
									<Button className="btn-primary" onClick={() => setShowDeActivateModal(true)}>
										Deactivate organisation
									</Button>
								</div>
							)}

							<GlobalModal isOpen={showAdminsModal} setIsOpen={setShowAdminsModal} titleText="ChildAware Leaders">
								<AdminsModal name={name} id={companiesDetailData.id} getCompaniesDetailData={getCompaniesDetailData} />
							</GlobalModal>

							<GlobalModal isOpen={showEnableModal} setIsOpen={setShowEnableModal} titleText="Activate organisation">
								<EnableModal companiesDetailData={companiesDetailData} />
							</GlobalModal>

							<GlobalModal
								isOpen={showDeActivateModal}
								setIsOpen={setShowDeActivateModal}
								titleText="Deactivate organisation"
							>
								<DeActivateModal companiesDetailData={companiesDetailData} />
							</GlobalModal>

							<GlobalModal
								isOpen={showExportModal}
								setIsOpen={setShowExportModal}
								titleText="Export data"
								closeText="Cancel"
							>
								<ExportDataModal organisationId={companiesDetailData.id} onExportStarted={handleExportStarted} />
							</GlobalModal>

							<GlobalModal
								isOpen={showDeleteModal}
								setIsOpen={setShowDeleteModal}
								titleText="Delete organisation"
								closeText="Cancel"
							>
								<InitiateDeleteOrganisationModal
									organisationId={companiesDetailData.id}
									onDeletionRequested={handleDeletionRequested}
								/>
							</GlobalModal>

							<GlobalModal
								isOpen={showDeclineDeleteModal}
								setIsOpen={setShowDeclineDeleteModal}
								titleText="Decline deletion"
								closeText="Cancel"
							>
								<DeclineDeleteOrganisationModal
									organisationId={companiesDetailData.id}
									onDeclineComplete={handleDeclineComplete}
								/>
							</GlobalModal>

							<GlobalModal
								isOpen={showApproveDeleteModal}
								setIsOpen={setShowApproveDeleteModal}
								titleText="Delete organisation"
								closeText="Cancel"
							>
								<ApproveDeleteOrganisationModal organisationId={companiesDetailData.id} />
							</GlobalModal>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default CompaniesDetail;
